import type { ProjectScope } from '@/features/sync/project-scope';
import { ACTION_ITEM_FLAG_FIELD } from '@/types/shared';

import type { TranscriptSegment } from './types';

const pendingTranscriptSegmentActionItemFlags = new Map<string, boolean>();

function pendingKey(scope: ProjectScope, sessionId: string, segmentId: string): string {
    return `${scope.orgId}/${scope.projectId}/${sessionId}/${segmentId}`;
}

function isFlagged(segment: TranscriptSegment): boolean {
    return segment[ACTION_ITEM_FLAG_FIELD] === true;
}

function withActionItemFlag(segment: TranscriptSegment, flagged: boolean): TranscriptSegment {
    const next = { ...segment };
    if (flagged) {
        next[ACTION_ITEM_FLAG_FIELD] = true;
    } else {
        delete next[ACTION_ITEM_FLAG_FIELD];
    }
    return next;
}

export function markPendingTranscriptSegmentActionItemFlagWrite(
    scope: ProjectScope,
    segment: TranscriptSegment
): void {
    pendingTranscriptSegmentActionItemFlags.set(
        pendingKey(scope, segment.sessionId, segment.id),
        isFlagged(segment)
    );
}

export function isPendingTranscriptSegmentActionItemFlagWrite(
    scope: ProjectScope,
    sessionId: string,
    segmentId: string
): boolean {
    return pendingTranscriptSegmentActionItemFlags.has(pendingKey(scope, sessionId, segmentId));
}

export function clearPendingTranscriptSegmentActionItemFlagWrites(): void {
    pendingTranscriptSegmentActionItemFlags.clear();
}

export function mergeTranscriptSegmentsWithPendingActionItemFlags(
    scope: ProjectScope,
    remoteSegments: readonly TranscriptSegment[],
    localSegments: readonly TranscriptSegment[]
): TranscriptSegment[] {
    const remoteIds = new Set(remoteSegments.map(segment => segment.id));
    const merged = remoteSegments.map(remoteSegment => {
        const key = pendingKey(scope, remoteSegment.sessionId, remoteSegment.id);
        const pendingFlag = pendingTranscriptSegmentActionItemFlags.get(key);
        if (pendingFlag === undefined) {
            return remoteSegment;
        }

        if (isFlagged(remoteSegment) === pendingFlag) {
            pendingTranscriptSegmentActionItemFlags.delete(key);
            return remoteSegment;
        }

        return withActionItemFlag(remoteSegment, pendingFlag);
    });

    for (const localSegment of localSegments) {
        if (remoteIds.has(localSegment.id)) {
            continue;
        }
        if (pendingTranscriptSegmentActionItemFlags.has(pendingKey(scope, localSegment.sessionId, localSegment.id))) {
            merged.push(localSegment);
        }
    }

    return merged;
}
